import { z } from 'zod';
import { objectId } from './schemas.js';
import { EXPENSE_CATEGORIES, FINANCE_SCOPES, } from './types.js';
/**
 * Buying things: who the company buys from, and the paper trail for each
 * purchase before the money leaves.
 *
 * The expense side of the books already records money that has gone out. This
 * is the half that comes first — someone needs a thing, somebody who is
 * allowed to spend agrees, the thing arrives, and only then is it a cost. An
 * expense claim made after the fact cannot answer "who said yes", and that is
 * the question a purchase order exists to answer.
 *
 * The journey ends inside the existing books rather than beside them: marking
 * an order received posts a matching approved expense against the same scope,
 * so a workspace that adopts purchase orders does not end up with two
 * different answers to what it spent.
 */
/* ------------------------------- vendors -------------------------------- */
export const VENDOR_STATUSES = ['active', 'archived'];
/**
 * How long after invoicing the vendor expects to be paid, offered as the
 * terms people actually write on paper. Stored as plain days so a due date is
 * arithmetic rather than a lookup.
 */
export const PAYMENT_TERMS = [0, 7, 14, 30, 45, 60, 90];
export const createVendorSchema = z.object({
    name: z.string().trim().min(2).max(120),
    contactName: z.string().trim().max(120).optional(),
    email: z.string().trim().email().max(200).optional().or(z.literal('')),
    phone: z.string().trim().max(40).optional(),
    website: z.string().trim().max(200).optional(),
    address: z.string().trim().max(300).optional(),
    taxId: z.string().trim().max(60).optional(),
    paymentTermsDays: z.number().int().min(0).max(180).default(30),
    category: z.enum(EXPENSE_CATEGORIES).default('services'),
    status: z.enum(VENDOR_STATUSES).default('active'),
    note: z.string().trim().max(2000).optional(),
});
export const updateVendorSchema = createVendorSchema.partial();
export const vendorQuerySchema = z.object({
    status: z.enum(VENDOR_STATUSES).optional(),
    q: z.string().trim().max(80).optional(),
});
/* --------------------------- purchase orders ---------------------------- */
/**
 * The life of an order, and the only path through it.
 *
 * `draft` is the requester's, `submitted` is the reviewer's, and the split
 * between `approved` and `received` is the point of the whole module: an
 * approved order is a promise, a received one is a cost. Skipping straight
 * from submitted to received would lose the approval, which is the record
 * anybody ever comes back to look for.
 *
 * `cancelled` is terminal and available from anywhere before receipt, because
 * orders die for reasons the workflow does not model — the vendor withdrew,
 * the project was dropped, somebody ordered it twice.
 */
export const PURCHASE_ORDER_STATUSES = [
    'draft',
    'submitted',
    'approved',
    'rejected',
    'received',
    'cancelled',
];
/** What the requester may still change, and what the reviewer may act on. */
export const isEditableOrder = (status) => status === 'draft' || status === 'rejected';
const orderLine = z.object({
    description: z.string().trim().min(1).max(200),
    quantity: z.number().int().min(1).max(100_000),
    unitAmount: z.number().int().nonnegative().max(1_000_000_000_000),
});
/**
 * Scope and its id have to agree: a department-scoped order needs a
 * department and nothing else, and a workspace-scoped one needs neither.
 * Typed on `string` rather than `FinanceScope` so zod can hand it the partial
 * shape a patch produces.
 */
const scopedToItsId = (body) => {
    if (body.scope === 'department')
        return Boolean(body.departmentId) && !body.projectId;
    if (body.scope === 'project')
        return Boolean(body.projectId) && !body.departmentId;
    if (body.scope === 'workspace')
        return !body.departmentId && !body.projectId;
    return true;
};
const SCOPE_MESSAGE = 'Charge it to a department or a project, or to the whole workspace';
export const createPurchaseOrderSchema = z
    .object({
    vendorId: objectId,
    title: z.string().trim().min(2).max(140),
    lines: z.array(orderLine).min(1).max(50),
    scope: z.enum(FINANCE_SCOPES).default('workspace'),
    departmentId: objectId.optional(),
    projectId: objectId.optional(),
    category: z.enum(EXPENSE_CATEGORIES).optional(),
    expectedDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-16')
        .optional(),
    note: z.string().trim().max(2000).optional(),
})
    .refine(scopedToItsId, SCOPE_MESSAGE);
const updateOrderFields = z.object({
    vendorId: objectId.optional(),
    title: z.string().trim().min(2).max(140).optional(),
    lines: z.array(orderLine).min(1).max(50).optional(),
    scope: z.enum(FINANCE_SCOPES).optional(),
    departmentId: objectId.nullable().optional(),
    projectId: objectId.nullable().optional(),
    category: z.enum(EXPENSE_CATEGORIES).optional(),
    expectedDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-16')
        .nullable()
        .optional(),
    note: z.string().trim().max(2000).optional(),
});
export const updatePurchaseOrderSchema = updateOrderFields.refine((body) => body.scope === undefined || scopedToItsId(body), SCOPE_MESSAGE);
export const reviewPurchaseOrderSchema = z.object({
    decision: z.enum(['approved', 'rejected']),
    reviewNote: z.string().trim().max(500).optional(),
});
/**
 * Receiving an order posts it to the books, so the receiver gets to correct
 * the figure: an order for ten of something that arrived as nine, or a vendor
 * whose invoice came in a few units off, should post what was actually spent
 * rather than what was asked for.
 */
export const receivePurchaseOrderSchema = z.object({
    amount: z.number().int().nonnegative().max(1_000_000_000_000).optional(),
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-16')
        .optional(),
    note: z.string().trim().max(500).optional(),
});
export const purchaseOrderQuerySchema = z.object({
    status: z.enum(PURCHASE_ORDER_STATUSES).optional(),
    vendorId: objectId.optional(),
    /** `mine` is what the requester sees; the reviewer's queue is `submitted`. */
    view: z.enum(['all', 'mine', 'queue']).default('all'),
});
export const orderTotal = (lines) => lines.reduce((sum, line) => sum + line.quantity * line.unitAmount, 0);
